import React, { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { EnvelopeOpenIcon, ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import {
  usePrepareRunAnnouncementMutation,
  useSendPreparedRunAnnouncementMutation,
} from "../../app/api/console/notificationsApiSlice";
import { captureException } from "../../app/sentry";
import {
  formatMutationError,
  shouldReportMutationFailure,
  toCaptureError,
} from "../../app/utils/mutationError";

// Platform's approved-send fail-closed contract (never resent silently).
const ARTIFACT_MISMATCH_ERRORS = new Set(["artifact_missing", "artifact_changed"]);
const ALREADY_SENT_WARNING =
  "A run-announcement notification was already sent for this run. Pass override=true to resend.";
const MISSING_ARTIFACT_ERROR =
  "Prepare did not return a reviewable artifact (version, hash, and HTML preview are required).";

function isAlreadySentResponse(result) {
  return (
    !result?.sent &&
    (result?.skip_reason === "already_sent" ||
      result?.platform?.skip_reason === "already_sent" ||
      result?.warning === ALREADY_SENT_WARNING ||
      result?.platform?.warning === ALREADY_SENT_WARNING)
  );
}

/** Prepare is only reviewable when the approved-send identity fields are present. */
export function isReviewablePrepareResult(result) {
  return Boolean(
    result &&
      typeof result.artifact_version === "string" &&
      result.artifact_version &&
      typeof result.content_hash === "string" &&
      result.content_hash &&
      typeof result.html_preview === "string" &&
      result.html_preview
  );
}

function prepareFailureMessage(result) {
  if (!result) return "Failed to prepare preview.";
  if (result.warning || result.skip_reason || result.state === "skipped") {
    return (
      result.warning ||
      result.skip_reason ||
      "Preview was skipped — nothing to review or send."
    );
  }
  return MISSING_ARTIFACT_ERROR;
}

function makeItemState(item) {
  return {
    ...item,
    approved: false,
    approvalDecision: "pending", // pending | approved | skipped
    prepareStatus: "idle", // idle | loading | ready | error
    prepareError: null,
    artifact: null,
    sendStatus: "idle", // idle | loading | sent | error | blocked | already_sent
    sendError: null,
    sendResult: null,
  };
}

function formatTimestamp(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return String(iso);
  }
}

function EmailPreviewFrame({ html, subject }) {
  // Email HTML is typically ~600px wide and taller than a short card.
  // Use a viewport-relative height so operators can read the full preview
  // without nested H+V scrolling inside a cramped iframe.
  return (
    <iframe
      data-testid="email-preview-frame"
      srcDoc={html || ""}
      sandbox=""
      referrerPolicy="no-referrer"
      title={`Email preview: ${subject || "Run-complete email"}`}
      className="w-full h-[min(60vh,36rem)] min-h-80 rounded-xl border border-border-subtle bg-white"
    />
  );
}

function StatusBadge({ item }) {
  if (item.prepareStatus === "loading") {
    return (
      <span
        aria-live="polite"
        className="text-caption font-medium text-text-tertiary"
      >
        Preparing…
      </span>
    );
  }
  if (item.prepareStatus === "error") {
    return (
      <span
        aria-live="polite"
        className="text-caption font-medium text-severity-high"
      >
        Preview failed
      </span>
    );
  }
  if (item.prepareStatus === "ready" && item.artifact) {
    return (
      <span
        aria-live="polite"
        className="text-caption font-medium text-text-tertiary"
      >
        {item.artifact.cached
          ? "Cached email preview"
          : `Generated ${formatTimestamp(item.artifact.generated_at)}`}
      </span>
    );
  }
  return null;
}

function ItemSendResult({ item }) {
  if (item.sendStatus === "sent") {
    const sentCount = item.sendResult?.sent_count;
    const hasSentCount = Number.isFinite(sentCount);
    return (
      <p
        data-testid={`send-result-${item.key}`}
        aria-live="polite"
        className="text-body-xs font-medium text-accent-green"
      >
        {hasSentCount
          ? `Sent to ${sentCount} ${
              sentCount === 1 ? "recipient" : "recipients"
            }.`
          : "Email sent."}
      </p>
    );
  }
  if (item.sendStatus === "blocked") {
    return (
      <p
        data-testid={`send-result-${item.key}`}
        aria-live="polite"
        className="text-body-xs font-medium text-severity-high"
      >
        The reviewed preview is out of date — regenerate and review again before
        sending.
      </p>
    );
  }
  if (item.sendStatus === "already_sent") {
    return (
      <p
        data-testid={`send-result-${item.key}`}
        aria-live="polite"
        className="text-body-xs font-medium text-text-secondary"
      >
        This production run was already sent. Send it again only if you intend
        to resend the reviewed email.
      </p>
    );
  }
  if (item.sendStatus === "error") {
    return (
      <p
        data-testid={`send-result-${item.key}`}
        aria-live="polite"
        className="text-body-xs font-medium text-severity-high"
      >
        {item.sendError || "Failed to send email."}
      </p>
    );
  }
  if (item.sendResult?.warning) {
    return (
      <p
        data-testid={`send-result-${item.key}`}
        aria-live="polite"
        className="text-body-xs font-medium text-text-secondary"
      >
        {item.sendResult.warning}
      </p>
    );
  }
  return null;
}

function QueueItemRow({
  item,
  isActive,
  onSelect,
  onToggleApprove,
  onRetryPrepare,
  onRegenerate,
  onRetrySend,
  onForceResend,
}) {
  const canApprove =
    item.prepareStatus === "ready" &&
    item.sendStatus !== "sent" &&
    item.sendStatus !== "blocked" &&
    item.sendStatus !== "already_sent";
  const label = item.label || item.scanId;
  return (
    <li
      data-testid={`queue-item-${item.key}`}
      className={`rounded-xl border px-3 py-2.5 transition-colors ${
        isActive
          ? "border-accent-purple/40 bg-accent-purple/5"
          : "border-border-subtle bg-white hover:bg-surface-neutral"
      }`}
    >
      <div className="flex items-center gap-2.5">
        <input
          id={`approve-email-${item.key}`}
          type="checkbox"
          data-testid={`approve-${item.key}`}
          aria-label={`Approve email for ${label}`}
          checked={item.approved}
          disabled={!canApprove}
          onChange={(e) => onToggleApprove(item.key, e.target.checked)}
          className="h-4 w-4 rounded border-border-default text-accent-purple focus-visible:ring-accent-purple/30 disabled:opacity-40"
        />
        <button
          type="button"
          data-testid={`queue-select-${item.key}`}
          onClick={() => onSelect(item.key)}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              onSelect(item.key);
            }
          }}
          className="min-w-0 flex-1 rounded-lg text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-purple/30"
        >
          <p className="text-body-xs font-medium text-text-primary truncate">
            {label}
          </p>
          <StatusBadge item={item} />
        </button>
        {item.prepareStatus === "error" && (
          <button
            type="button"
            data-testid={`retry-prepare-${item.key}`}
            onClick={(e) => {
              e.stopPropagation();
              onRetryPrepare(item.key);
            }}
            className="rounded text-caption font-semibold text-accent-purple hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-purple/30"
          >
            Retry
          </button>
        )}
        {item.sendStatus === "blocked" && (
          <button
            type="button"
            data-testid={`regenerate-${item.key}`}
            onClick={(e) => {
              e.stopPropagation();
              onRegenerate(item.key);
            }}
            className="rounded text-caption font-semibold text-accent-purple hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-purple/30"
          >
            Regenerate
          </button>
        )}
        {item.sendStatus === "error" && (
          <button
            type="button"
            data-testid={`retry-send-${item.key}`}
            onClick={(e) => {
              e.stopPropagation();
              onRetrySend(item.key);
            }}
            className="rounded text-caption font-semibold text-accent-purple hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-purple/30"
          >
            Retry email
          </button>
        )}
        {item.sendStatus === "already_sent" && (
          <button
            type="button"
            data-testid={`force-resend-${item.key}`}
            onClick={(e) => {
              e.stopPropagation();
              onForceResend(item.key);
            }}
            className="rounded text-caption font-semibold text-accent-purple hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-purple/30"
          >
            Send again
          </button>
        )}
        {item.sendStatus === "sent" && (
          <span className="text-caption font-semibold text-accent-green">Sent</span>
        )}
      </div>
      {item.prepareStatus === "error" && item.prepareError && (
        <p className="mt-1 text-caption text-severity-high">{item.prepareError}</p>
      )}
      <ItemSendResult item={item} />
    </li>
  );
}

function NotificationEmailPreviewModal({
  isOpen,
  onClose,
  onQueueComplete,
  onPendingCountChange,
  mode = "single",
  items,
}) {
  const isQueue = mode === "queue";
  const initialStateRef = useRef((items || []).map(makeItemState));
  const [state, setState] = useState(initialStateRef.current);
  const stateRef = useRef(initialStateRef.current);
  const [activeKey, setActiveKey] = useState(() => items?.[0]?.key ?? null);
  const autoPreparedRef = useRef(false);
  const mountedRef = useRef(false);
  const sendingKeysRef = useRef(new Set());
  const completionReportedRef = useRef(false);

  const [triggerPrepare] = usePrepareRunAnnouncementMutation();
  const [triggerSend] = useSendPreparedRunAnnouncementMutation();

  const updateItem = useCallback((key, updater) => {
    if (!mountedRef.current) return stateRef.current;
    const next = stateRef.current.map((it) =>
      it.key === key ? { ...it, ...updater(it) } : it
    );
    stateRef.current = next;
    setState(next);
    if (isQueue) {
      const pendingCount = next.filter(
        (item) =>
          item.sendStatus !== "sent" && item.approvalDecision !== "skipped"
      ).length;
      onPendingCountChange?.(pendingCount);
    }
    return next;
  }, [isQueue, onPendingCountChange]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const reportQueueCompleteIfReady = useCallback(
    (nextState) => {
      if (
        !isQueue ||
        !mountedRef.current ||
        completionReportedRef.current ||
        !nextState.length
      ) {
        return;
      }
      const complete = nextState.every(
        (item) =>
          item.sendStatus === "sent" || item.approvalDecision === "skipped"
      );
      if (complete) {
        completionReportedRef.current = true;
        onQueueComplete?.();
      }
    },
    [isQueue, onQueueComplete]
  );

  const runPrepare = useCallback(
    async (item, { forceRegenerate = false } = {}) => {
      updateItem(item.key, () => ({ prepareStatus: "loading", prepareError: null }));
      try {
        const result = await triggerPrepare({
          scanId: item.scanId,
          runId: item.runId,
          useProd: item.useProd,
          firePath: item.firePath,
          triggeredBy: item.triggeredBy,
          forceRegenerate,
        }).unwrap();
        if (!mountedRef.current) return null;
        // HTTP 200 with state=skipped (or missing version/hash/preview) must
        // never become "ready" — Send would omit expected_* and silently
        // downgrade to an unreviewed normal send.
        if (!isReviewablePrepareResult(result)) {
          updateItem(item.key, () => ({
            prepareStatus: "error",
            prepareError: prepareFailureMessage(result),
            artifact: null,
            approved: false,
            approvalDecision: "pending",
            sendStatus: "idle",
            sendError: null,
            sendResult: null,
          }));
          return null;
        }
        const approved = isQueue && !forceRegenerate;
        updateItem(item.key, () => ({
          prepareStatus: "ready",
          prepareError: null,
          artifact: result,
          approved,
          approvalDecision: approved ? "approved" : "pending",
          sendStatus: "idle",
          sendError: null,
          sendResult: null,
        }));
        return result;
      } catch (err) {
        if (!mountedRef.current) return null;
        const detail = formatMutationError(err, "Failed to prepare preview.");
        updateItem(item.key, () => ({
          prepareStatus: "error",
          prepareError: String(detail),
          approved: false,
          approvalDecision: "pending",
        }));
        if (shouldReportMutationFailure(err)) {
          captureException(toCaptureError(err, detail), {
            notification_prepare: {
              scan_id: item.scanId,
              run_id: item.runId,
              use_prod: item.useProd,
              status: err?.status ?? null,
            },
          });
        }
        return null;
      }
    },
    [triggerPrepare, updateItem, isQueue]
  );

  const runSend = useCallback(
    async (item, { override = Boolean(item.hasBeenNotified) } = {}) => {
      if (!item.artifact || sendingKeysRef.current.has(item.key)) return null;
      if (!isReviewablePrepareResult(item.artifact)) {
        updateItem(item.key, () => ({
          sendStatus: "error",
          sendError: MISSING_ARTIFACT_ERROR,
          approved: false,
          approvalDecision: "pending",
        }));
        return null;
      }
      sendingKeysRef.current.add(item.key);
      updateItem(item.key, () => ({ sendStatus: "loading", sendError: null }));
      try {
        const result = await triggerSend({
          scanId: item.scanId,
          runId: item.runId,
          useProd: item.useProd,
          override,
          firePath: item.firePath,
          triggeredBy: item.triggeredBy,
          recipientEmails: item.recipientEmails,
          expectedArtifactVersion: item.artifact.artifact_version,
          expectedContentHash: item.artifact.content_hash,
        }).unwrap();

        if (!mountedRef.current) return null;
        const mismatch = !result?.sent && ARTIFACT_MISMATCH_ERRORS.has(result?.error);
        const alreadySent = isAlreadySentResponse(result);
        const nextState = updateItem(item.key, () => ({
          sendStatus: mismatch
            ? "blocked"
            : alreadySent
            ? "already_sent"
            : result?.sent
            ? "sent"
            : "error",
          approved: mismatch ? false : item.approved,
          approvalDecision: mismatch ? "pending" : item.approvalDecision,
          sendError: mismatch || alreadySent
            ? null
            : !result?.sent
            ? result?.warning || result?.skip_reason || result?.error || "Email not sent."
            : null,
          sendResult: result,
        }));
        if (result?.sent) reportQueueCompleteIfReady(nextState);
        return result;
      } catch (err) {
        if (!mountedRef.current) return null;
        const detail = formatMutationError(err, "Failed to send email.");
        updateItem(item.key, () => ({ sendStatus: "error", sendError: String(detail) }));
        if (shouldReportMutationFailure(err)) {
          captureException(toCaptureError(err, detail), {
            notification_send: {
              scan_id: item.scanId,
              run_id: item.runId,
              use_prod: item.useProd,
              status: err?.status ?? null,
            },
          });
        }
        return null;
      } finally {
        sendingKeysRef.current.delete(item.key);
      }
    },
    [triggerSend, updateItem, reportQueueCompleteIfReady]
  );

  // Auto-prepare every item once, in parallel, when a production-push review
  // queue opens. The manual single-item flow only prepares on demand.
  useEffect(() => {
    if (!isQueue || !isOpen || autoPreparedRef.current) return;
    autoPreparedRef.current = true;
    Promise.allSettled((items || []).map((item) => runPrepare(item)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isQueue, isOpen]);

  const handleSelect = (key) => setActiveKey(key);

  const handleToggleApprove = (key, checked) => {
    const nextState = updateItem(key, () => ({
      approved: checked,
      approvalDecision: checked ? "approved" : "skipped",
    }));
    if (!checked) reportQueueCompleteIfReady(nextState);
  };

  const handleRetryPrepare = (key) => {
    const item = state.find((it) => it.key === key);
    if (item) runPrepare(item);
  };

  const handleRegenerate = (key) => {
    const item = state.find((it) => it.key === key);
    if (item) runPrepare(item, { forceRegenerate: true });
  };

  const handleRetrySend = (key) => {
    const item = state.find((it) => it.key === key);
    if (item) runSend(item);
  };

  const handleForceResend = (key) => {
    const item = state.find((it) => it.key === key);
    if (item?.sendStatus === "already_sent") {
      runSend(item, { override: true });
    }
  };

  const handlePreview = () => {
    const item = state[0];
    if (item) runPrepare(item);
  };

  const handleSingleSend = () => {
    const item = state[0];
    if (item) runSend(item);
  };

  const handleSendApproved = () => {
    const targets = state.filter(
      (it) =>
        it.approved &&
        it.prepareStatus === "ready" &&
        (it.sendStatus === "idle" || it.sendStatus === "error")
    );
    Promise.allSettled(targets.map((it) => runSend(it)));
  };

  const activeItem = state.find((it) => it.key === activeKey) || state[0] || null;
  const activeIndex = state.findIndex((it) => it.key === activeItem?.key);
  const approvedReadyCount = state.filter(
    (it) =>
      it.approved &&
      it.prepareStatus === "ready" &&
      (it.sendStatus === "idle" || it.sendStatus === "error")
  ).length;

  const goPrev = () => {
    if (activeIndex > 0) setActiveKey(state[activeIndex - 1].key);
  };
  const goNext = () => {
    if (activeIndex >= 0 && activeIndex < state.length - 1) {
      setActiveKey(state[activeIndex + 1].key);
    }
  };

  const singleItem = state[0];
  const singleSendLabel = singleItem?.hasBeenNotified
    ? "Resend email"
    : "Send email";

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/30" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto overscroll-contain">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel
                data-testid="email-preview-panel"
                className={`w-full ${
                  isQueue ? "max-w-5xl" : "max-w-3xl"
                } max-h-[90vh] flex flex-col rounded-2xl bg-white p-6 text-left shadow-xl`}
              >
                <div className="flex shrink-0 items-start gap-3">
                  <div className="rounded-xl bg-accent-purple/10 p-2">
                    <EnvelopeOpenIcon
                      className="h-5 w-5 text-accent-purple"
                      aria-hidden="true"
                    />
                  </div>
                  <div className="min-w-0 flex-1">
                    <Dialog.Title className="text-body-lg font-semibold text-text-primary">
                      {isQueue ? "Review run-complete emails" : "Preview email"}
                    </Dialog.Title>
                    <p className="mt-1 text-body-xs text-text-secondary">
                      {isQueue
                        ? "Each successfully generated preview is approved by default. Review, then send only what you approve."
                        : "Preview the exact email before sending."}
                    </p>
                  </div>
                </div>

                <div
                  className={`mt-5 min-h-0 flex-1 overflow-y-auto grid grid-cols-1 gap-4 ${
                    isQueue ? "sm:grid-cols-4" : "sm:grid-cols-3"
                  }`}
                >
                  {isQueue && (
                    <ul className="space-y-2 sm:col-span-1 max-h-[min(50vh,28rem)] overflow-y-auto pr-1">
                      {state.map((item) => (
                        <QueueItemRow
                          key={item.key}
                          item={item}
                          isActive={item.key === activeItem?.key}
                          onSelect={handleSelect}
                          onToggleApprove={handleToggleApprove}
                          onRetryPrepare={handleRetryPrepare}
                          onRegenerate={handleRegenerate}
                          onRetrySend={handleRetrySend}
                          onForceResend={handleForceResend}
                        />
                      ))}
                    </ul>
                  )}

                  <div className={isQueue ? "sm:col-span-3" : "sm:col-span-3"}>
                    {!isQueue && state.length > 1 && (
                      <div className="mb-3 flex items-center justify-between">
                        <button
                          type="button"
                          data-testid="nav-prev"
                          aria-label="Previous email"
                          onClick={goPrev}
                          disabled={activeIndex <= 0}
                          className="rounded text-text-tertiary hover:text-text-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-purple/30 disabled:opacity-30"
                        >
                          <ChevronLeftIcon className="h-4 w-4" aria-hidden="true" />
                        </button>
                        <span className="text-caption text-text-tertiary">
                          {activeIndex + 1} of {state.length}
                        </span>
                        <button
                          type="button"
                          data-testid="nav-next"
                          aria-label="Next email"
                          onClick={goNext}
                          disabled={activeIndex >= state.length - 1}
                          className="rounded text-text-tertiary hover:text-text-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-purple/30 disabled:opacity-30"
                        >
                          <ChevronRightIcon className="h-4 w-4" aria-hidden="true" />
                        </button>
                      </div>
                    )}

                    {activeItem?.prepareStatus === "ready" && activeItem.artifact ? (
                      <div className="space-y-3">
                        <div>
                          <p className="text-body-sm font-semibold text-text-primary">
                            {activeItem.artifact.subject}
                          </p>
                          {activeItem.artifact.preheader && (
                            <p className="text-body-xs text-text-tertiary">
                              {activeItem.artifact.preheader}
                            </p>
                          )}
                        </div>
                        <p
                          className="text-caption text-text-tertiary line-clamp-2 break-all"
                          title={
                            activeItem.recipientEmails?.length
                              ? activeItem.recipientEmails.join(", ")
                              : undefined
                          }
                        >
                          Recipients:{" "}
                          {activeItem.recipientEmails?.length
                            ? activeItem.recipientEmails.join(", ")
                            : "None"}
                        </p>
                        <StatusBadge item={activeItem} />
                        <EmailPreviewFrame
                          html={activeItem.artifact.html_preview}
                          subject={activeItem.artifact.subject}
                        />
                        {activeItem.artifact.warning && (
                          <p className="text-body-xs text-text-secondary">
                            {activeItem.artifact.warning}
                          </p>
                        )}
                      </div>
                    ) : activeItem?.prepareStatus === "loading" ? (
                      <p className="text-body-xs text-text-tertiary py-10 text-center">
                        Generating preview…
                      </p>
                    ) : activeItem?.prepareStatus === "error" ? (
                      <p className="text-body-xs text-severity-high py-10 text-center">
                        {activeItem.prepareError}
                      </p>
                    ) : (
                      <p className="text-body-xs text-text-tertiary py-10 text-center">
                        No preview generated yet.
                      </p>
                    )}

                    {!isQueue && activeItem && <ItemSendResult item={activeItem} />}
                  </div>
                </div>

                <div className="mt-5 flex shrink-0 items-center justify-between border-t border-border-subtle pt-4">
                  <button
                    type="button"
                    onClick={onClose}
                    className="rounded-xl border border-border-default px-4 py-2 text-body-xs font-medium text-text-secondary hover:bg-surface-neutral focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-purple/30"
                  >
                    Close
                  </button>

                  {isQueue ? (
                    <button
                      type="button"
                      data-testid="send-approved-button"
                      disabled={approvedReadyCount === 0}
                      onClick={handleSendApproved}
                      className="rounded-xl bg-button-primary px-4 py-2 text-body-xs font-semibold text-white hover:bg-button-primary-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-purple/30 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      Send approved emails ({approvedReadyCount})
                    </button>
                  ) : singleItem?.prepareStatus === "ready" ? (
                    <button
                      type="button"
                      data-testid="send-button"
                      disabled={
                        singleItem.sendStatus === "loading" ||
                        singleItem.sendStatus === "sent"
                      }
                      onClick={handleSingleSend}
                      className="rounded-xl bg-button-primary px-4 py-2 text-body-xs font-semibold text-white hover:bg-button-primary-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-purple/30 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {singleItem.sendStatus === "loading"
                        ? "Sending…"
                        : singleItem.sendStatus === "sent"
                        ? "Email sent"
                        : singleSendLabel}
                    </button>
                  ) : (
                    <button
                      type="button"
                      data-testid="preview-button"
                      disabled={singleItem?.prepareStatus === "loading"}
                      onClick={handlePreview}
                      className="rounded-xl bg-button-primary px-4 py-2 text-body-xs font-semibold text-white hover:bg-button-primary-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-purple/30 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {singleItem?.prepareStatus === "loading" ? "Loading…" : "Preview email"}
                    </button>
                  )}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}

export default NotificationEmailPreviewModal;
